import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "./firebase";
import { collection, addDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { Button, Select, Input, DatePicker, TimePicker, message } from "antd";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

export default function NFCPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState(""); // '' | 'reporter' | 'sortie'
  const [tables, setTables] = useState([]);
  const [form, setForm] = useState({
    type: "",
    tablesConcernees: [],
    dateIncident: dayjs(),
    heureIncident: dayjs(),
    commentaire: "",
  });

  // ✅ Generate tables 1 → 57 automatically
  useEffect(() => {
    const generatedTables = Array.from({ length: 57 }, (_, i) => i + 1);
    setTables(generatedTables);
  }, []);

  const handleSubmit = async () => {
    if (!form.type || form.tablesConcernees.length === 0) {
      message.error("Veuillez remplir tous les champs requis.");
      return;
    }

    try {
      // 1️⃣ Save the incident to Firestore
      await addDoc(collection(db, "incidents"), {
        tableSource: Number(id),
        type: form.type,
        tablesConcernees: form.tablesConcernees,
        dateIncident: form.dateIncident.toDate(),
        heureIncident: form.heureIncident.toDate(),
        commentaire: form.commentaire,
        createdAt: new Date(),
      });

      // 2️⃣ Send phone notification through ntfy.sh
      await fetch("https://ntfy.sh/westudy_incidents", {
        method: "POST",
        headers: {
          "Title": "Nouveau incident signale",
          "Priority": "high",
          "Content-Type": "text/plain; charset=utf-8",
        },
        body: `🚨 Nouveau incident signalé !
Table: ${id}
Type: ${form.type}
Tables concernées: ${form.tablesConcernees.join(", ")}
Commentaire: ${form.commentaire || "Aucun commentaire"}`,
      });

      // 3️⃣ Success feedback
      message.success("✅ Incident signalé avec succès !");
      setMode("");
      setForm({
        type: "",
        tablesConcernees: [],
        dateIncident: dayjs(),
        heureIncident: dayjs(),
        commentaire: "",
      });
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'enregistrement de l'incident.");
    }
  };

  // Home screen
  if (!mode) {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "1.5rem",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "24px",
            padding: "2rem",
            marginBottom: "2rem",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h1
            style={{
              fontSize: "3rem",
              margin: 0,
              color: "#fff",
              fontWeight: "700",
              textAlign: "center",
            }}
          >
            Table {id}
          </h1>
        </div>

        <div style={{ width: "100%", maxWidth: "400px" }}>
          <Button
            type="primary"
            size="large"
            icon={<FileTextOutlined />}
            style={{
              backgroundColor: "#fff",
              color: "#667eea",
              border: "none",
              borderRadius: "16px",
              height: "auto",
              padding: "1.5rem",
              fontSize: "1.125rem",
              fontWeight: "600",
              marginBottom: "1rem",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onClick={() => setMode("reporter")}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onTouchStart={(e) =>
              (e.currentTarget.style.transform = "scale(0.98)")
            }
            onTouchEnd={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Reporter un incident
          </Button>

          <Button
            danger
            size="large"
            icon={<ClockCircleOutlined />}
            style={{
              backgroundColor: "#ff4757",
              color: "#fff",
              border: "none",
              borderRadius: "16px",
              height: "auto",
              padding: "1.5rem",
              fontSize: "1.125rem",
              fontWeight: "600",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              boxShadow: "0 4px 16px rgba(255, 71, 87, 0.3)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onClick={() => navigate(`/timer/${id}`)}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onTouchStart={(e) =>
              (e.currentTarget.style.transform = "scale(0.98)")
            }
            onTouchEnd={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Temps de sortie
          </Button>
        </div>
      </div>
    );
  }

  // Incident form
  if (mode === "reporter") {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "100vh",
          padding: "1rem",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "1.5rem",
            paddingTop: "0.5rem",
          }}
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined style={{ fontSize: "1.25rem" }} />}
            onClick={() => setMode("")}
            style={{
              color: "#fff",
              padding: "0.5rem",
              marginRight: "1rem",
            }}
          />
          <h1
            style={{
              color: "#fff",
              fontSize: "1.5rem",
              fontWeight: "700",
              margin: 0,
            }}
          >
            Signaler un incident
          </h1>
        </div>

        {/* Form Card */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "20px",
            padding: "1.5rem",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          {/* Type d'incident */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "0.5rem",
              }}
            >
              Type d'incident <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <Select
              placeholder="Sélectionnez un type"
              size="large"
              style={{ width: "100%" }}
              value={form.type}
              onChange={(value) => setForm({ ...form, type: value })}
            >
              <Option value="Bruit">🔊 Bruit</Option>
              <Option value="Harcèlement sexuel">⚠️ Harcèlement sexuel</Option>
              <Option value="Prise électrique défectueuse">
                🔌 Prise électrique défectueuse
              </Option>
              <Option value="Lampe ne fonctionne pas">
                💡 Lampe ne fonctionne pas
              </Option>
              <Option value="Autre">📋 Autre</Option>
            </Select>
          </div>

          {/* Tables concernées */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "0.5rem",
              }}
            >
              Tables concernées <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <Select
              mode="multiple"
              placeholder="Sélectionnez une ou plusieurs tables"
              size="large"
              style={{ width: "100%" }}
              value={form.tablesConcernees}
              onChange={(values) =>
                setForm({ ...form, tablesConcernees: values })
              }
              maxTagCount="responsive"
            >
              {tables.map((t) => (
                <Option key={t} value={t}>
                  Table {t}
                </Option>
              ))}
            </Select>
          </div>

          {/* Date + Time */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "0.5rem",
                }}
              >
                Date
              </label>
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                value={form.dateIncident}
                onChange={(date) => setForm({ ...form, dateIncident: date })}
                format="DD/MM/YYYY"
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "0.5rem",
                }}
              >
                Heure
              </label>
              <TimePicker
                size="large"
                style={{ width: "100%" }}
                value={form.heureIncident}
                onChange={(time) => setForm({ ...form, heureIncident: time })}
                format="HH:mm"
              />
            </div>
          </div>

          {/* Commentaire */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "0.5rem",
              }}
            >
              Commentaire
            </label>
            <TextArea
              rows={4}
              placeholder="Ajoutez des détails supplémentaires (optionnel)"
              value={form.commentaire}
              onChange={(e) =>
                setForm({ ...form, commentaire: e.target.value })
              }
              style={{ borderRadius: "12px", fontSize: "1rem" }}
              maxLength={500}
              showCount
            />
          </div>

          {/* Submit */}
          <Button
            type="primary"
            size="large"
            block
            icon={<CheckCircleOutlined />}
            onClick={handleSubmit}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              borderRadius: "12px",
              height: "auto",
              padding: "1rem",
              fontSize: "1rem",
              fontWeight: "600",
              boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
              transition: "transform 0.2s",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Envoyer le rapport
          </Button>

          <p
            style={{
              fontSize: "0.75rem",
              color: "#6b7280",
              textAlign: "center",
              marginTop: "1rem",
              marginBottom: 0,
            }}
          >
            <span style={{ color: "#ef4444" }}>*</span> Champs obligatoires
          </p>
        </div>
      </div>
    );
  }
}
